"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileUploadCardContext = exports.FileUploadCard = void 0;
/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
const React = __importStar(require("react"));
const index_js_1 = require("../utils/index.js");
const classnames_1 = __importDefault(require("classnames"));
const FileEmptyCard_js_1 = require("./FileEmptyCard.js");
const Anchor_js_1 = require("../Typography/Anchor/Anchor.js");
require("@itwin/itwinui-css/css/file-upload.css");
const toBytes = (bytes) => {
    const units = [' bytes', 'KB', 'MB', 'GB', 'TB'];
    let i = 0;
    while (bytes >= 1024 && ++i) {
        bytes = bytes / 1024;
    }
    return bytes.toFixed(bytes < 10 && i > 0 ? 2 : 0) + units[i];
};
const toDate = (dateNumber) => {
    const date = new Date(dateNumber);
    return date.toDateString() + ' ' + date.toLocaleTimeString();
};
const FileUploadCardIcon = React.forwardRef((props, ref) => {
    const { children, className, ...rest } = props;
    return (React.createElement("span", { className: (0, classnames_1.default)('iui-file-card-icon', className), ref: ref, ...rest }, children !== null && children !== void 0 ? children : React.createElement(index_js_1.SvgDocument, null)));
});
FileUploadCardIcon.displayName = 'FileUploadCard.Icon';
const FileUploadCardInfo = React.forwardRef((props, ref) => {
    const { children, className, ...rest } = props;
    return (React.createElement("span", { className: (0, classnames_1.default)('iui-file-card-text', className), ref: ref, ...rest }, children));
});
FileUploadCardInfo.displayName = 'FileUploadCard.Info';
const FileUploadCardTitle = React.forwardRef((props, ref) => {
    const { children, className, ...rest } = props;
    const { files } = (0, index_js_1.useSafeContext)(exports.FileUploadCardContext);
    const title = files.length > 1 ? files.length + ' files selected' : files[0].name;
    return (React.createElement("span", { className: (0, classnames_1.default)('iui-file-card-title', className), ref: ref, ...rest }, children !== null && children !== void 0 ? children : title));
});
FileUploadCardTitle.displayName = 'FileUploadCard.Title';
const FileUploadCardDescription = React.forwardRef((props, ref) => {
    const { children, className, ...rest } = props;
    const { files } = (0, index_js_1.useSafeContext)(exports.FileUploadCardContext);
    let description = files.length > 1
        ? files[0].name + ', ' + files[1].name
        : toBytes(files[0].size) + ' ' + toDate(files[0].lastModified);
    if (files.length > 2) {
        description += ', and ' + (files.length - 2) + ' others';
    }
    return (React.createElement("span", { className: (0, classnames_1.default)('iui-file-card-description', className), ref: ref, ...rest }, children !== null && children !== void 0 ? children : description));
});
FileUploadCardDescription.displayName = 'FileUploadCard.Description';
const FileUploadCardAction = React.forwardRef((props, ref) => {
    const { children, className, ...rest } = props;
    return (React.createElement("div", { className: (0, classnames_1.default)('iui-file-card-action', className), ref: ref, ...rest }, children));
});
FileUploadCardAction.displayName = 'FileUploadCard.Action';
const FileUploadCardInputLabel = React.forwardRef((props, ref) => {
    const { children, ...rest } = props;
    const { inputId } = (0, index_js_1.useSafeContext)(exports.FileUploadCardContext);
    return (React.createElement(Anchor_js_1.Anchor, { as: 'label', ref: ref, htmlFor: inputId, ...rest }, children));
});
FileUploadCardInputLabel.displayName = 'FileUploadCard.InputLabel';
const FileUploadCardInput = React.forwardRef((props, ref) => {
    const { children, onChange, id, ...rest } = props;
    const { files, onFilesChange, setInternalFiles, inputId, setInputId } = (0, index_js_1.useSafeContext)(exports.FileUploadCardContext);
    const setNativeFilesRef = React.useCallback((node) => {
        var _a;
        if (!node || !((_a = (0, index_js_1.getWindow)()) === null || _a === void 0 ? void 0 : _a.DataTransfer)) {
            return;
        }
        const dataTransfer = new DataTransfer();
        dataTransfer.items.clear();
        Array.from(files).forEach((file) => dataTransfer.items.add(file));
        node.files = dataTransfer.files;
    }, [files]);
    const refs = (0, index_js_1.useMergedRefs)(ref, setNativeFilesRef);
    React.useEffect(() => {
        if (id && id !== inputId) {
            setInputId(id);
        }
    }, [id, inputId, setInputId]);
    return (React.createElement(React.Fragment, null,
        React.createElement(index_js_1.VisuallyHidden, { as: 'input', type: 'file', onChange: (0, index_js_1.mergeEventHandlers)(onChange, (e) => {
                const _files = Array.from(e.currentTarget.files || []);
                onFilesChange === null || onFilesChange === void 0 ? void 0 : onFilesChange(_files);
                setInternalFiles(_files);
            }), ref: refs, id: id !== null && id !== void 0 ? id : inputId, ...rest }),
        children));
});
FileUploadCardInput.displayName = 'FileUploadCard.Input';
/**
 * Default card to be used with the `FileUpload` wrapper component for single-file uploading.
 * @example
 * <FileUploadCard />
 * <FileUploadCard files={files} onFilesChange={(files) => setFiles(files)}>
 *   <FileUploadCard.Icon>
 *     <SvgSmileyHappyVery />
 *   </FileUploadCard.Icon>
 *   <FileUploadCard.Info>
 *     <FileUploadCard.Title>Custom File Name</FileUploadCard.Title>
 *     <FileUploadCard.Description>
 *       Custom File Description
 *     </FileUploadCard.Description>
 *   </FileUploadCard.Info>
 *   <FileUploadCard.Action>
 *     <Button
 *       onClick={() => {
 *         setFiles([]);
 *       }}
 *     />
 *     <FileUploadCard.Input name={fileInputId} ref={inputRef} />
 *   </FileUploadCard.Action>
 * </FileUploadCard>
 */
exports.FileUploadCard = Object.assign(React.forwardRef((props, ref) => {
    var _a;
    const { className, children, files: filesProp, onFilesChange, emptyCard = React.createElement(FileEmptyCard_js_1.FileEmptyCard, null), input, ...rest } = props;
    const [internalFiles, setInternalFiles] = React.useState();
    const uid = (0, index_js_1.useId)();
    const [inputId, setInputId] = React.useState(uid);
    const files = (_a = filesProp !== null && filesProp !== void 0 ? filesProp : internalFiles) !== null && _a !== void 0 ? _a : [];
    return (React.createElement(exports.FileUploadCardContext.Provider, { value: {
            files,
            onFilesChange,
            setInternalFiles,
            inputId,
            setInputId,
        } },
        (files === null || files === void 0 ? void 0 : files.length) ? (React.createElement("div", { className: (0, classnames_1.default)('iui-file-card', className), ref: ref, ...rest }, children !== null && children !== void 0 ? children : (React.createElement(React.Fragment, null,
            React.createElement(exports.FileUploadCard.Icon, null),
            React.createElement(exports.FileUploadCard.Info, null,
                React.createElement(exports.FileUploadCard.Title, null),
                React.createElement(exports.FileUploadCard.Description, null)),
            React.createElement(exports.FileUploadCard.Action, null,
                React.createElement(exports.FileUploadCard.InputLabel, null, "Replace")))))) : (emptyCard), input !== null && input !== void 0 ? input : React.createElement(exports.FileUploadCard.Input, null)));
}), {
    Icon: FileUploadCardIcon,
    Info: FileUploadCardInfo,
    Title: FileUploadCardTitle,
    Description: FileUploadCardDescription,
    Action: FileUploadCardAction,
    InputLabel: FileUploadCardInputLabel,
    Input: FileUploadCardInput,
});
exports.FileUploadCard.displayName = 'FileUploadCard';
exports.default = exports.FileUploadCard;
exports.FileUploadCardContext = React.createContext(undefined);
